import { useEffect, useState, useRef } from 'react';
import { APIProvider, Map, Marker, useMapsLibrary } from '@vis.gl/react-google-maps';
import { axiosInstance, Field, inputCls } from './_shared';

// ─── Constants ────────────────────────────────────────────────────────────────
// Google Maps API key (build-time env). When missing, MapLocationPicker falls
// back to manual lat/lng inputs so the form is still usable in environments
// without Maps configured.
export const MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Default map centre when the venue has no lat/lng yet — Mumbai.
export const DEFAULT_CENTER = { lat: 19.076, lng: 72.877 };

function MapPickerInner({ venue, onVenueChange }) {
  const inputRef = useRef(null);
  const placesLib = useMapsLibrary('places');
  const [markerPos, setMarkerPos] = useState(
    venue.lat && venue.lng
      ? { lat: parseFloat(venue.lat), lng: parseFloat(venue.lng) }
      : DEFAULT_CENTER
  );

  // Set up Places Autocomplete
  useEffect(() => {
    if (!placesLib || !inputRef.current) return;
    const ac = new placesLib.Autocomplete(inputRef.current, {
      fields: ['geometry', 'formatted_address', 'name', 'address_components'],
    });
    ac.addListener('place_changed', () => {
      const place = ac.getPlace();
      if (!place?.geometry?.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      const city =
        place.address_components?.find(c => c.types.includes('locality'))?.long_name ||
        place.address_components?.find(c => c.types.includes('administrative_area_level_2'))?.long_name ||
        '';
      const newPos = { lat, lng };
      setMarkerPos(newPos);
      onVenueChange({
        name: place.name || venue.name,
        address: place.formatted_address || '',
        city,
        lat,
        lng,
      });
      // Clear input display so placeholder is visible
      if (inputRef.current) inputRef.current.value = place.formatted_address || place.name || '';
    });
  }, [placesLib]); // eslint-disable-line react-hooks/exhaustive-deps

  const reverseGeocode = async (lat, lng) => {
    try {
      const res = await axiosInstance.post('/locations/reverse-geocode', { latitude: lat, longitude: lng });
      onVenueChange({
        ...venue,
        address: res.data.formatted_address || '',
        city: res.data.city || '',
        lat,
        lng,
      });
    } catch {
      onVenueChange({ ...venue, lat, lng });
    }
  };

  const handleMapClick = e => {
    if (!e.detail?.latLng) return;
    const { lat, lng } = e.detail.latLng;
    setMarkerPos({ lat, lng });
    reverseGeocode(lat, lng);
  };

  const handleDragEnd = e => {
    if (!e.latLng) return;
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPos({ lat, lng });
    reverseGeocode(lat, lng);
  };

  return (
    <div className="space-y-2">
      <div className="relative">
        <i className="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none" />
        <input
          ref={inputRef}
          placeholder="Search venue or address…"
          className={`${inputCls} pl-9`}
        />
      </div>
      <div className="h-48 rounded-xl overflow-hidden border border-gray-200 shadow-sm">
        <Map
          defaultZoom={12}
          defaultCenter={markerPos}
          center={markerPos}
          onClick={handleMapClick}
          gestureHandling="greedy"
          disableDefaultUI
        >
          <Marker
            position={markerPos}
            draggable
            onDragEnd={handleDragEnd}
          />
        </Map>
      </div>
      {venue.lat && venue.lng && (
        <p className="text-xs text-gray-400 flex items-center gap-1">
          <i className="ph-bold ph-map-pin text-[#E54562]" />
          {parseFloat(venue.lat).toFixed(5)}, {parseFloat(venue.lng).toFixed(5)}
        </p>
      )}
    </div>
  );
}

function MapLocationPicker({ venue, onVenueChange }) {
  if (!MAPS_API_KEY) {
    return (
      <div className="grid grid-cols-2 gap-3">
        <Field label="Lat"><input type="number" className={inputCls} value={venue.lat || ''} onChange={e => onVenueChange({ ...venue, lat: parseFloat(e.target.value) || null })} placeholder="e.g. 19.0760" /></Field>
        <Field label="Lng"><input type="number" className={inputCls} value={venue.lng || ''} onChange={e => onVenueChange({ ...venue, lng: parseFloat(e.target.value) || null })} placeholder="e.g. 72.8777" /></Field>
      </div>
    );
  }
  return (
    <APIProvider apiKey={MAPS_API_KEY} libraries={['places']}>
      <MapPickerInner venue={venue} onVenueChange={onVenueChange} />
    </APIProvider>
  );
}

// ─── Tab: Events ───────────────────────────────────────────────────────────────

// ─── Admin Registration View ────────────────────────────────────────────────
// ─── Admin Phases View ─────────────────────────────────────────────────────────
export const PHASE_DEFS = {
  overview: { title: 'Overview', icon: 'ph-info', description: 'General tournament information' },
  register: { title: 'Registration', icon: 'ph-clipboard-text', description: 'Player, team owner, sponsor registration' },
  teams: { title: 'Teams', icon: 'ph-users-three', description: 'Teams, owners and squads' },
  auction: { title: 'Auction', icon: 'ph-gavel', description: 'Player auction phase' },
  fixtures: { title: 'Fixtures', icon: 'ph-calendar-dots', description: 'Match schedules' },
  live: { title: 'Live', icon: 'ph-broadcast', description: 'Live match updates' },
  results: { title: 'Results', icon: 'ph-trophy', description: 'Tournament standings and results' },
};

export { MapPickerInner };
export default MapLocationPicker;
